import { useMutation } from 'Data/Api.js'
import mutationCheckInPatient from './mutation-check-in-patient.graphql.js'
import mutationSeat from './mutation-seat.graphql.js'
import mutationUnseat from './mutation-unseat.graphql.js'
import mutationCheckout from './mutation-check-out.graphql.js'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import { normalizePath, useSetFlowTo } from 'Simple/Flow.js'
import { useDataValue, useDataSubmit, useDataChange } from 'Simple/Data.js'
import {
  isUnscheduledAppointment,
  parseLocalDatetimeStringToZonedDate,
} from 'Data/format.js'
import { DEFAULT_TIMEZONE } from 'Data/constants.js'
import { useMemo } from 'react'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let onActionSeat = useDataOnActionSeat(props, data)
  let onActionUnseat = useDataOnActionUnseat(props, data)
  let onActionCheckout = useDataOnActionCheckOut(props, data)
  let onActionSchedule = useDataOnActionSchedule(props, data)

  return async function onSubmit({ args, ...params }) {
    switch (args.type) {
      case 'seat': {
        await onActionSeat({ args, ...params })
        break
      }
      case 'unseat': {
        await onActionUnseat({ args, ...params })
        break
      }
      case 'checkOut': {
        await onActionCheckout({ args, ...params })
        break
      }
      case 'reschedule':
      case 'schedule': {
        await onActionSchedule({ args, ...params })
        break
      }
      default: {
      }
    }
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSeat(props, data) {
  let id = useDataValue({
    context: 'event',
    viewPath: props.viewPath,
    path: 'appointment.booking.0._id',
  })

  let [, executeMutation] = useMutation(mutationSeat)
  // @ts-ignore
  let [, notify] = useNotifications()

  return async function onAction({ args }) {
    if (!args.chair_id || !args.provider_id || !args.assistant_id) {
      return true
    }

    let mutationResponse = await executeMutation({
      id,
      provider_id: args.provider_id,
      assistant_id: args.assistant_id,
      chair_id: args.chair_id,
    })
    if (mutationResponse.error) {
      notify(
        notifyError(
          `There was a problem seating the patient. Please try again.`
        )
      )
      return
    }

    notify(notifySuccess(`The patient was successfully seated.`))
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionUnseat(props, data) {
  let id = useDataValue({
    context: 'event',
    viewPath: props.viewPath,
    path: 'appointment.booking.0._id',
  })

  let [, executeMutation] = useMutation(mutationUnseat)
  // @ts-ignore
  let [, notify] = useNotifications()

  let setFlowTo = useSetFlowTo(props.viewPath)

  return async function onAction({ args }) {
    let mutationResponse = await executeMutation({
      id,
      skip_required_fields: args?.skip_required_fields || false,
    })

    if (mutationResponse.error) {
      notify(
        notifyError('Cannot unseat selected appointment. Please try again.')
      )
      return
    }

    if (
      mutationResponse.data.vaxiom_appointment_booking_unseat.status ===
      'missing_required_treatment_fields'
    ) {
      setFlowTo(
        normalizePath(
          props.viewPath,
          'Unseat/Content/MissingRequiredTreatmentFieldsDialog/Content'
        )
      )
    }

    notify(notifySuccess(`The patient was successfully unseated.`))
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionCheckOut(props, data) {
  let id = useDataValue({
    context: 'event',
    viewPath: props.viewPath,
    path: 'appointment.booking.0._id',
  })

  let [, executeMutation] = useMutation(mutationCheckout)
  // @ts-ignore
  let [, notify] = useNotifications()

  return async function onAction() {
    let mutationResponse = await executeMutation({ id })

    if (mutationResponse.error) {
      notify(notifyError('Cannot checkout patient. Please try again.'))
      return
    }

    notify(notifySuccess(`The patient was successfully checked out.`))
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
function useDataOnActionSchedule(props, data) {
  let submit = useDataSubmit({
    viewPath: props.viewPath,
    context: 'global',
  })
  let change = useDataChange({
    viewPath: props.viewPath,
    context: 'global',
  })
  let patient_id = useDataValue({
    viewPath: props.viewPath,
    context: 'patient',
    path: '_id',
  })
  let appointment = useDataValue({
    viewPath: props.viewPath,
    context: 'event',
    path: 'appointment',
  })
  let tx_uuid = useDataValue({
    viewPath: props.viewPath,
    context: 'appointments',
    path: 'tx_uuid',
  })
  let treatment_plans = useDataValue({
    viewPath: props.viewPath,
    context: 'treatment_plans',
  })
  let global = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
  })

  let tx = useMemo(() => {
    if (!tx_uuid || !treatment_plans) return null
    return treatment_plans.find(i => i._id === tx_uuid)
  }, [tx_uuid, treatment_plans])

  return async function onAction() {
    let isUnscheduled = isUnscheduledAppointment(appointment)
    let sync = {
      patient_id,
      appointment_id: appointment._id,
      location: isUnscheduled
        ? tx?.organization
        : appointment.booking[0].chair.resource.organization,
      ...(!isUnscheduled && {
        date: parseLocalDatetimeStringToZonedDate(
          appointment.booking.local_start_date,
          appointment.booking[0].chair.resource.organization.time_zone_id ||
            DEFAULT_TIMEZONE
        ),
      }),
      is_patient_overlay_call: true,
    }
    if (global.tab_id !== 'Calendar') {
      await submit({
        type: 'tabs/add',
        tab: {
          id: 'Calendar',
          name: 'Calendar',
          type: 'Calendar',
          viewPath: '/App/Account/Content/Calendar',
          sync,
        },
      })
    } else {
      change(next => {
        next.tabs = next.tabs.map(tab =>
          tab.id === 'Calendar' ? { ...tab, sync } : tab
        )
      })
    }
  }
}
